'use client';
import React, { useEffect, useRef, useState } from 'react';

type OperatingModel = 'OTR (Amazon Contract)' | 'Regional' | 'Street & Spot' | 'Not Sure Yet';
type HomeTimePreference = '2–3 weeks out' | 'Home weekly' | 'Home 2x/week' | 'Flexible';

interface FormState {
  name: string;
  phone: string;
  email: string;
  operatingModel: OperatingModel | '';
  homeTime: HomeTimePreference | '';
  truckYear: string;
  message: string;
}

const operatingModels: OperatingModel[] = ['OTR (Amazon Contract)', 'Regional', 'Street & Spot', 'Not Sure Yet'];
const homeTimePreferences: HomeTimePreference[] = ['2–3 weeks out', 'Home weekly', 'Home 2x/week', 'Flexible'];

const programBenefits = [
  { icon: '💰', label: 'No Escrow', detail: 'We do not charge an escrow fee for the program.' },
  { icon: '📋', label: 'No Hidden Fees', detail: 'Fixed deductions are clearly explained before onboarding.' },
  { icon: '⛽', label: 'Fuel Card Provided', detail: 'Fuel card sent with your setup package.' },
  { icon: '📦', label: 'Setup Package Mailed', detail: 'IFTA, decals, fuel card, and ELD device shipped to your home.' },
  { icon: '🚛', label: 'Drop & Hook', detail: 'Power only operation — Amazon provides trailers on contract freight.' },
  { icon: '📅', label: 'On-Time Payments', detail: 'Consistent, on-time payments with transparent pay structure.' },
];

const CarrierMatchCTA: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const revealRefs = useRef<(HTMLElement | null)[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    name: '',
    phone: '',
    email: '',
    operatingModel: '',
    homeTime: '',
    truckYear: '',
    message: '',
  });

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    revealRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const text =
      `📋 *New Owner-Operator Application*\n\n` +
      `👤 *Name:* ${form.name}\n` +
      `📞 *Phone:* ${form.phone}\n` +
      `📧 *Email:* ${form.email}\n` +
      `🚛 *Truck Year:* ${form.truckYear || 'Not provided'}\n` +
      `📦 *Preferred Program:* ${form.operatingModel || 'Not selected'}\n` +
      `🏠 *Home Time Preference:* ${form.homeTime || 'Not selected'}\n` +
      `💬 *Additional Info:* ${form.message || 'None'}`;

    try {
      const res = await fetch(
        `https://api.telegram.org/bot8719407941:AAGCQQ7jgucZAsGLL5HHVcbRTNTIoRcTZz4/sendMessage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: '-1004374445638',
            text,
            parse_mode: 'Markdown',
          }),
        }
      );

      if (!res.ok) {
        throw new Error('Failed to send message');
      }

      setForm({ name: '', phone: '', email: '', operatingModel: '', homeTime: '', truckYear: '', message: '' });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="cta-section" className="py-32 relative bg-plum-deep/50" ref={sectionRef}>
      <div className="beam-border-h" style={{ top: 0, bottom: 'auto' }} />
      <div className="dot-pattern absolute inset-0 opacity-20 pointer-events-none" />

      {/* Background violet glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-violet/8 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          className="reveal mb-16 text-center max-w-3xl mx-auto space-y-4"
          ref={(el) => { revealRefs.current[0] = el; }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-violet">
            Apply Now · No Obligation · Talk to Recruiting
          </p>
          <h2 className="text-display font-bold tracking-tight leading-tight text-cream">
            Ready to Keep Your
            <br />
            <span className="text-silver-lavender/60">Truck Moving?</span>
          </h2>
          <p className="text-lg text-silver-lavender/60 leading-relaxed font-light max-w-2xl mx-auto">
            Fill out the form below and our recruiting team will reach out to discuss
            available freight programs, lane options, and next steps to get you started.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: program benefits */}
          <div
            className="lg:col-span-4 space-y-5 reveal"
            ref={(el) => { revealRefs.current[1] = el; }}>
            <p className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-4">
              What You Get
            </p>
            <div className="space-y-3">
              {programBenefits.map((b) => (
                <div
                  key={b.label}
                  className="bg-plum-card border border-silver-lavender/12 rounded-xl p-4 flex gap-3 hover:border-violet/25 transition-colors">
                  <span className="text-xl shrink-0">{b.icon}</span>
                  <div>
                    <p className="text-sm font-bold text-cream">{b.label}</p>
                    <p className="text-xs text-silver-lavender/50 font-light mt-0.5 leading-relaxed">{b.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Company info */}
            <div id="contact" className="bg-violet/10 border border-violet/20 rounded-2xl p-5 space-y-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet/70">Contact Recruiting</p>
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-bold text-cream">Velocity Trucking LLC</p>
                  <p className="text-xs text-silver-lavender/50 font-light">Sister company: FIA 1331 Transport LLC</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-violet text-sm shrink-0">📍</span>
                  <p className="text-xs text-silver-lavender/60 font-light leading-relaxed">
                    2318 E Main Street<br />Lancaster, OH 43130
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: application form */}
          <div
            className="lg:col-span-8 reveal reveal-delay-2"
            ref={(el) => { revealRefs.current[2] = el; }}>
            {!submitted ? (
              <form
                onSubmit={handleSubmit}
                className="bg-plum-card border border-silver-lavender/15 rounded-2xl p-8 space-y-7 relative overflow-hidden">
                {/* Glow accent */}
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-violet/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-cream mb-1">Apply to the Owner-Operator Program</h3>
                  <p className="text-sm text-silver-lavender/50 font-light">Tell us about your operation and we&apos;ll be in touch.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 relative z-10">
                  {/* Name */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/60">
                      Your Name
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      placeholder="First and last name"
                      required
                      className="w-full bg-plum-deep/60 border border-silver-lavender/15 text-cream placeholder:text-silver-lavender/25 px-4 py-3 text-sm font-medium rounded-xl focus:outline-none focus:border-violet/50 transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/60">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => updateForm('phone', e.target.value)}
                      placeholder="(555) 000-0000"
                      required
                      className="w-full bg-plum-deep/60 border border-silver-lavender/15 text-cream placeholder:text-silver-lavender/25 px-4 py-3 text-sm font-medium rounded-xl focus:outline-none focus:border-violet/50 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/60">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full bg-plum-deep/60 border border-silver-lavender/15 text-cream placeholder:text-silver-lavender/25 px-4 py-3 text-sm font-medium rounded-xl focus:outline-none focus:border-violet/50 transition-colors"
                    />
                  </div>

                  {/* Truck Year */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/60">
                      Truck Year
                    </label>
                    <input
                      type="text"
                      value={form.truckYear}
                      onChange={(e) => updateForm('truckYear', e.target.value)}
                      placeholder="e.g. 2019"
                      className="w-full bg-plum-deep/60 border border-silver-lavender/15 text-cream placeholder:text-silver-lavender/25 px-4 py-3 text-sm font-medium rounded-xl focus:outline-none focus:border-violet/50 transition-colors"
                    />
                  </div>

                  {/* Operating Model */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/60">
                      Preferred Program
                    </label>
                    <select
                      value={form.operatingModel}
                      onChange={(e) => updateForm('operatingModel', e.target.value)}
                      required
                      className="w-full bg-plum-deep/60 border border-silver-lavender/15 text-cream px-4 py-3 text-sm font-medium rounded-xl focus:outline-none focus:border-violet/50 transition-colors appearance-none cursor-pointer">
                      <option value="" disabled className="bg-plum-black">Select a program</option>
                      {operatingModels.map((m) => (
                        <option key={m} value={m} className="bg-plum-black">{m}</option>
                      ))}
                    </select>
                  </div>

                  {/* Home Time Preference */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/60">
                      Home Time Preference
                    </label>
                    <select
                      value={form.homeTime}
                      onChange={(e) => updateForm('homeTime', e.target.value)}
                      required
                      className="w-full bg-plum-deep/60 border border-silver-lavender/15 text-cream px-4 py-3 text-sm font-medium rounded-xl focus:outline-none focus:border-violet/50 transition-colors appearance-none cursor-pointer">
                      <option value="" disabled className="bg-plum-black">Select preference</option>
                      {homeTimePreferences.map((h) => (
                        <option key={h} value={h} className="bg-plum-black">{h}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2 relative z-10">
                  <label className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/60">
                    Additional Info (Optional)
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => updateForm('message', e.target.value)}
                    placeholder="Tell us about your operation, home state, or any questions..."
                    rows={3}
                    className="w-full bg-plum-deep/60 border border-silver-lavender/15 text-cream placeholder:text-silver-lavender/25 px-4 py-3 text-sm font-medium rounded-xl focus:outline-none focus:border-violet/50 transition-colors resize-none"
                  />
                </div>

                <div className="relative z-10">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-violet text-cream py-4 text-[11px] font-bold uppercase tracking-widest rounded-full hover:bg-violet-bright transition-all duration-300 cta-glow disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? 'Sending...' : 'Submit Application'}
                  </button>
                  {error && (
                    <p className="text-red-400 text-xs mt-3 text-center font-medium">{error}</p>
                  )}
                  {!error && (
                    <p className="text-[11px] text-silver-lavender/35 mt-3 text-center font-medium">
                      Our recruiting team will contact you within 1 business day.
                    </p>
                  )}
                </div>
              </form>
            ) : (
              <div className="bg-plum-card border border-violet/30 rounded-2xl p-12 text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-violet/20 flex items-center justify-center mx-auto">
                  <div className="w-8 h-8 rounded-full bg-violet flex items-center justify-center">
                    <span className="text-cream text-lg">✓</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-cream mb-2">Application Received</h3>
                  <p className="text-silver-lavender/60 font-light leading-relaxed max-w-md mx-auto">
                    Thank you for your interest in Velocity Trucking LLC. Our recruiting team
                    will reach out within 1 business day to discuss available programs and next steps.
                  </p>
                </div>
                <div className="bg-violet/10 border border-violet/20 rounded-xl p-4 text-sm text-silver-lavender/60 font-light">
                  📍 2318 E Main Street, Lancaster, OH 43130
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CarrierMatchCTA;